import { OAUTH_POPUP_REDIRECT_PARAM } from '../../containers/flora-agent/components/agent-integrations/agent-integrations.helpers';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  GoogleDriveFile,
  GoogleFileAccessResponse,
  GoogleFilesResponse,
  GoogleIntegrationStatus,
  GoogleOAuthStartResponse,
  GooglePickerTokenResponse,
} from './google-integrations-client.type';

const axiosInstance = AxiosInstance();
const baseURL = `${appConfig.VITE_APP_API_URL}/integrations/google`;
const INTEGRATIONS_PAGE_PATH = '/application/flora-agent/integrations';

/**
 * Error thrown by Google integration calls that need to expose the backend
 * `detail` code (e.g. `google_reconnect_required`) so callers can branch on it.
 */
class GoogleIntegrationError extends Error {
  /** The backend `detail` string, when present. */
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'GoogleIntegrationError';
    this.code = code;
  }
}

/**
 * Fetch the Google authorization URL for connecting the CURRENT session user.
 *
 * This authenticated start endpoint binds the OAuth exchange to the session
 * user, so the callback saves credentials to whoever clicked Connect even
 * when the authorized Google account's email differs from their login email.
 *
 * @param {string} organizationId - The organization context for the flow.
 * @returns {Promise<string>} The Google authorization URL for the popup.
 */
function getUserAuthStartUrl(organizationId: string): Promise<string> {
  const url = `${baseURL}/user-auth/start`;
  return axiosInstance
    .get<GoogleOAuthStartResponse>(url, {
      params: {
        organization_id: organizationId,
        // The `oauth_popup` marker tells the integrations landing route it is
        // running inside the OAuth popup so it closes itself. It rides in the
        // URL because `window.name` is cleared by the cross-site OAuth hops.
        redirect_path: `${INTEGRATIONS_PAGE_PATH}?${OAUTH_POPUP_REDIRECT_PARAM}=1`,
      },
    })
    .then((response) => response.data.url)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to start the Google connection';
      console.error('[Google] getUserAuthStartUrl error:', message);
      throw new Error(message);
    });
}

/**
 * Fetch the current user's Google connection status for an organization.
 *
 * @param {string} organizationId - The organization to check.
 * @returns {Promise<GoogleIntegrationStatus>} Status object including connected/google_email/scopes.
 */
function fetchGoogleStatus(organizationId: string): Promise<GoogleIntegrationStatus> {
  const url = `${baseURL}/status`;
  return axiosInstance
    .get<GoogleIntegrationStatus>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to fetch Google status';
      console.error('[Google] fetchGoogleStatus error:', message);
      throw new Error(message);
    });
}

/**
 * Disconnect the current user's Google account from the given org.
 *
 * @param {string} organizationId - The organization whose user-auth to soft-delete.
 * @returns {Promise<void>} Resolves when the user-auth row has been soft-deleted.
 */
async function disconnectGoogle(organizationId: string): Promise<void> {
  const url = `${baseURL}/user-auth`;
  await axiosInstance.delete(url, { params: { organization_id: organizationId } }).catch((error) => {
    const message = error.response?.data?.detail || error.message || 'Failed to disconnect your Google account';
    console.error('[Google] disconnectGoogle error:', message);
    throw new Error(message);
  });
}

/**
 * Check whether Flora's Google integration can access a specific Drive file.
 *
 * @param {string} organizationId - The organization context for the check.
 * @param {string} fileId - The Google Drive/Sheets file id to probe.
 * @returns {Promise<boolean>} True when the file is accessible to Flora.
 */
function fetchFileAccess(organizationId: string, fileId: string): Promise<boolean> {
  const url = `${baseURL}/file-access`;
  return axiosInstance
    .get<GoogleFileAccessResponse>(url, { params: { organization_id: organizationId, file_id: fileId } })
    .then((response) => response.data.accessible)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to check Google file access';
      console.error('[Google] fetchFileAccess error:', message);
      throw new Error(message);
    });
}

/**
 * Fetch a short-lived OAuth token and app id for launching the Google Picker.
 *
 * On failure the thrown {@link GoogleIntegrationError} carries the backend
 * `detail` on both its `message` and `code` so callers can branch on
 * `error.code === 'google_reconnect_required'`.
 *
 * @param {string} organizationId - The organization context for the token.
 * @returns {Promise<GooglePickerTokenResponse>} The picker access token and app id.
 */
function fetchPickerToken(organizationId: string): Promise<GooglePickerTokenResponse> {
  const url = `${baseURL}/picker-token`;
  return axiosInstance
    .get<GooglePickerTokenResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const detail = error.response?.data?.detail;
      const message = detail || error.message || 'Failed to fetch a Google picker token';
      console.error('[Google] fetchPickerToken error:', message);
      throw new GoogleIntegrationError(message, detail);
    });
}

/**
 * Fetch the Google Drive files Flora's integration can access for an org.
 *
 * Under the drive.file scope this returns only files Flora created or that the
 * user granted via the Picker. On failure the thrown
 * {@link GoogleIntegrationError} carries the backend `detail` on both its
 * `message` and `code` so callers can branch on `google_reconnect_required`.
 *
 * @param {string} organizationId - The organization context for the listing.
 * @returns {Promise<GoogleDriveFile[]>} The accessible Drive files, newest-first.
 */
function fetchGoogleFiles(organizationId: string): Promise<GoogleDriveFile[]> {
  const url = `${baseURL}/files`;
  return axiosInstance
    .get<GoogleFilesResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data.files)
    .catch((error) => {
      const detail = error.response?.data?.detail;
      const message = detail || error.message || 'Failed to list Google Drive files';
      console.error('[Google] fetchGoogleFiles error:', message);
      throw new GoogleIntegrationError(message, detail);
    });
}

export {
  disconnectGoogle,
  fetchFileAccess,
  fetchGoogleFiles,
  fetchGoogleStatus,
  fetchPickerToken,
  GoogleIntegrationError,
  getUserAuthStartUrl,
};
